import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, X, CheckCircle, ArrowLeft, Home, ChevronDown, Building2, Users, Eraser } from 'lucide-react';
import InvoiceCanvas from './canvas/InvoiceCanvas';
import SignUpGate from './SignUpGate';
import EmailInvoiceModal from './EmailInvoiceModal';
import UpgradeModal from './UpgradeModal';
import { useInvoiceForm } from '../hooks/useInvoiceForm';
import { useAutoSave, loadSavedData, clearSavedData } from '../hooks/useAutoSave';
import { useValidation } from '../hooks/useValidation';
import { useCalculations } from '../hooks/useCalculations';
import { useGuestLimit, saveGuestDraft } from '../hooks/useGuestLimit';
import { useAuth } from '../contexts/AuthContext';
import { downloadPDF, fetchInvoiceTypeConfig, finalizeInvoice, generateGuestInvoice, updateInvoice, getInvoice, listProfiles, listClients, saveDraft } from '../services/api';
import { useEntitlements, Features } from '../hooks/useEntitlements';
import {
  AUTOSAVE_DELAY_MS,
  MAX_LOGO_SIZE_BYTES,
  LOGO_MAX_DIMENSIONS
} from '../utils/constants';
import { validateForPdfGeneration } from '../utils/validators';
import {
  getInvoiceTypeUI,
  INVOICE_TYPES_UI,
  mergeConfigWithUI,
  getFieldVisibility,
  getLabels,
  getCurrencyConfig,
  getTaxConfig
} from '../config/invoiceTypes';

/**
 * Toast notification component for success/error messages
 * Auto-dismisses after specified duration
 */
function Toast({ message, type = 'success', onClose, duration = 5000 }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    
    return () => clearTimeout(timer);
  }, [onClose, duration]);

  const bgColor = type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  const textColor = type === 'success' ? 'text-green-800' : 'text-red-800';
  const Icon = type === 'success' ? CheckCircle : AlertCircle;
  const iconColor = type === 'success' ? 'text-green-500' : 'text-red-500';

  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg ${bgColor} animate-slideUp`}>
      <Icon className={iconColor} size={20} />
      <span className={`text-sm font-medium ${textColor}`}>{message}</span>
      <button
        onClick={onClose}
        className={`ml-2 ${textColor} hover:opacity-70`}
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}

/**
 * Main Invoice Generator Component
 * 
 * BACKEND IS SINGLE SOURCE OF TRUTH for invoice behavior.
 * Fetches config from: GET /api/invoice-types/{invoiceType}
 * 
 * Handles:
 * - Loading backend invoice type configuration
 * - Loading app configuration (tax rates, currencies)
 * - Auto-save functionality
 * - Draft recovery
 * - Logo upload and resize
 * - PDF generation and download
 * - Form validation (UX-only, backend validates business rules)
 */
export default function InvoiceGenerator() {
  // Get invoice type from URL params
  const { invoiceType = 'freelancer' } = useParams();
  
  // Get navigation location for prefill support (Feature #9: Duplicate Invoice)
  const location = useLocation();
  const navigate = useNavigate();
  const prefillDraft = location.state?.prefillDraft;
  const editInvoiceId = location.state?.editInvoiceId ?? null;
  const editInvoiceNumber = location.state?.editInvoiceNumber ?? null;
  const isEditMode = Boolean(editInvoiceId);
  const draftId = location.state?.draftId ?? null;

  const [activeDraftId, setActiveDraftId] = useState(draftId);
  const [draftSaving, setDraftSaving] = useState(false);
  
  // Get UI metadata for display (frontend only)
  const uiMetadata = useMemo(() => getInvoiceTypeUI(invoiceType), [invoiceType]);
  
  // Backend config state (SINGLE SOURCE OF TRUTH for behavior)
  const [backendConfig, setBackendConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  
  // Merged config: backend behavior + frontend UI metadata
  const invoiceConfig = useMemo(() => {
    if (!backendConfig) return null;
    
    // Merge backend config with UI metadata
    const merged = mergeConfigWithUI(backendConfig, invoiceType);
    
    // Add derived properties that components expect
    return {
      ...merged,
      // Field visibility derived from backend enabled_fields
      fields: getFieldVisibility(backendConfig),
      // Labels from backend with fallbacks
      labels: getLabels(backendConfig),
      // Currency config from backend
      currency: getCurrencyConfig(backendConfig),
      // Tax config from backend
      tax: getTaxConfig(backendConfig),
    };
  }, [backendConfig, invoiceType]);
  
  // Auth & guest state
  const { user } = useAuth();
  const { isFeatureEnabled, refresh: refreshEntitlements } = useEntitlements();
  const canSaveDraft = user && isFeatureEnabled(Features.INVOICE_DRAFT_SAVE);
  const { isLimitReached, increment } = useGuestLimit();
  const [showSignUpGate, setShowSignUpGate] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, reason: '' });

  // Profile picker state (multi-profile quick-fill)
  const [profiles, setProfiles] = useState([]);
  const [pickerProfileId, setPickerProfileId] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  // Client picker state (address book quick-fill)
  const [clients, setClients] = useState([]);
  const [pickerClientId, setPickerClientId] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);

  // UI State
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  // Cache the finalized PDF so Print and Download share one record/invoice number
  const [finalizedPDF, setFinalizedPDF] = useState(null); // { blob, filename }
  const finalizedAtRef = useRef(null); // formData reference at finalization time

  // Toast notification state
  const [toast, setToast] = useState(null);

  // Invoice type switcher state
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [pendingSwitchType, setPendingSwitchType] = useState(null);
  const typeDropdownRef = useRef(null);

  // Draft Recovery State
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);
  
  // Validation hook
  const validation = useValidation();

  // Auto-save key specific to this invoice type
  const autoSaveKey = useMemo(() => `invoice_draft_${invoiceType}`, [invoiceType]);

  // Load backend config on mount.
  // The active flag prevents the StrictMode double-mount from applying
  // stale results from the first (discarded) mount.
  useEffect(() => {
    let active = true;

    async function initialize() {
      setDataLoading(true);
      setConfigError(null);

      try {
        // Invoice type config is the single source of truth — tax default rate,
        // currency, field visibility, etc. all come from here.
        const config = await fetchInvoiceTypeConfig(invoiceType);

        if (!active) return;

        setBackendConfig(config);

        // In edit mode or draft mode we prefill from stored data — skip local draft recovery
        if (isEditMode || draftId) return;

        setTimeout(() => {
          if (!active) return;
          const draft = loadSavedData(autoSaveKey);
          if (draft?.formData) {
            const hasData = Boolean(
              draft.formData.business_name ||
              draft.formData.client_name ||
              draft.formData.business_email
            );
            if (hasData) {
              setSavedDraft(draft);
              setShowRecoveryDialog(true);
            }
          }
        }, 300);
      } catch (error) {
        if (!active) return;
        console.error('Failed to load config:', error);
        setConfigError(error.message || 'Failed to load invoice configuration');
      } finally {
        if (active) setDataLoading(false);
      }
    }

    initialize();

    return () => { active = false; };
  }, [autoSaveKey, invoiceType, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize form with loaded configuration
  const { formData, setFormData, updateFormData, resetForm } = useInvoiceForm({}, {}, invoiceConfig);

  // Edit mode: fetch the stored invoice and prefill the form
  useEffect(() => {
    if (!isEditMode || !editInvoiceId || dataLoading || !invoiceConfig) return;

    let active = true;
    getInvoice(editInvoiceId).then((invoice) => {
      if (!active) return;
      const src = invoice.data || {};
      const merged = {
        ...formData,
        business_name: src.business_name || '',
        business_email: src.business_email || '',
        business_phone: src.business_phone || '',
        business_address: src.business_address || '',
        business_city: src.business_city || '',
        business_state: src.business_state || '',
        business_zip: src.business_zip || '',
        business_country: src.business_country || '',
        business_gstin: src.business_gstin || '',
        client_name: src.client_name || '',
        client_email: src.client_email || '',
        client_phone: src.client_phone || '',
        client_address: src.client_address || '',
        client_city: src.client_city || '',
        client_state: src.client_state || '',
        client_zip: src.client_zip || '',
        client_country: src.client_country || '',
        client_gstin: src.client_gstin || '',
        invoice_date: src.invoice_date || formData.invoice_date,
        due_date: src.due_date || formData.due_date,
        po_number: src.po_number || '',
        notes: src.notes || '',
        terms: src.terms || '',
        currency: src.currency || formData.currency,
        tax_rate: src.tax_rate ?? formData.tax_rate,
        discount: src.discount ?? 0,
        place_of_supply: src.place_of_supply || '',
        bank_name: src.bank_name || '',
        bank_account: src.bank_account || '',
        bank_swift: src.bank_swift || '',
        bank_iban: src.bank_iban || '',
        items: (src.line_items || []).map(item => ({
          description: item.description || '',
          quantity: item.quantity ?? 1,
          rate: item.rate ?? 0,
        })),
        invoice_type: invoiceType,
      };
      setFormData(merged);
      if (src.signature_image) setSignaturePreview(src.signature_image);
      window.history.replaceState({}, document.title);
    }).catch((err) => {
      if (!active) return;
      console.error('Failed to load invoice for editing:', err);
      showToast('Failed to load invoice data', 'error');
    });

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editInvoiceId, dataLoading, invoiceConfig]);

  // Draft mode: fetch the stored draft and prefill the form
  useEffect(() => {
    if (!draftId || dataLoading || !invoiceConfig) return;

    let active = true;
    getInvoice(draftId).then((invoice) => {
      if (!active) return;
      const src = invoice.data || {};
      const merged = {
        ...formData,
        business_name: src.business_name || '',
        business_email: src.business_email || '',
        business_phone: src.business_phone || '',
        business_address: src.business_address || '',
        business_city: src.business_city || '',
        business_state: src.business_state || '',
        business_zip: src.business_zip || '',
        business_country: src.business_country || '',
        business_gstin: src.business_gstin || '',
        client_name: src.client_name || '',
        client_email: src.client_email || '',
        client_phone: src.client_phone || '',
        client_address: src.client_address || '',
        client_city: src.client_city || '',
        client_state: src.client_state || '',
        client_zip: src.client_zip || '',
        client_country: src.client_country || '',
        client_gstin: src.client_gstin || '',
        invoice_date: src.invoice_date || formData.invoice_date,
        due_date: src.due_date || formData.due_date,
        po_number: src.po_number || '',
        notes: src.notes || '',
        terms: src.terms || '',
        currency: src.currency || formData.currency,
        tax_rate: src.tax_rate ?? formData.tax_rate,
        discount: src.discount ?? 0,
        place_of_supply: src.place_of_supply || '',
        bank_name: src.bank_name || '',
        bank_account: src.bank_account || '',
        bank_swift: src.bank_swift || '',
        bank_iban: src.bank_iban || '',
        items: (src.line_items || []).map(item => ({
          description: item.description || '',
          quantity: item.quantity ?? 1,
          rate: item.rate ?? 0,
        })),
        business_logo: src.business_logo || null,
        signature_image: src.signature_image || null,
        authorized_signatory: src.authorized_signatory || '',
        invoice_type: invoiceType,
      };
      setFormData(merged);
      if (src.business_logo) setLogoPreview(src.business_logo);
      if (src.signature_image) setSignaturePreview(src.signature_image);
    }).catch((err) => {
      if (!active) return;
      console.error('Failed to load draft:', err);
      showToast('Failed to load draft data', 'error');
    });

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, dataLoading, invoiceConfig]);

  // Profile autofill: when logged in, load saved profiles and show picker on fresh forms.
  useEffect(() => {
    if (!user || isEditMode || draftId || prefillDraft || dataLoading || !invoiceConfig) return;
    let active = true;
    listProfiles().then(profs => {
      if (!active || !profs.length) return;
      setProfiles(profs);
      setPickerProfileId(String(profs[0].id));
      setShowPicker(true);
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isEditMode, dataLoading, invoiceConfig]);

  // Client picker: load saved clients and show picker on fresh forms.
  useEffect(() => {
    if (!user || isEditMode || draftId || prefillDraft || dataLoading || !invoiceConfig) return;
    let active = true;
    listClients().then(cls => {
      if (!active || !cls.length) return;
      setClients(cls);
      setPickerClientId(String(cls[0].id));
      setShowClientPicker(true);
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isEditMode, dataLoading, invoiceConfig]);

  // FEATURE #9: Handle prefillDraft from navigation state (Duplicate Invoice)
  // Apply prefill data once after form is initialized
  useEffect(() => {
    if (prefillDraft && formData && !dataLoading && invoiceConfig) {
      // Merge prefill data with current form data
      // Use fresh dates (today/due date) instead of old ones
      const mergedData = {
        ...formData,
        ...prefillDraft,
        // Ensure invoice_type matches the current route
        invoice_type: invoiceType,
        // Keep fresh dates if not provided in prefill
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
      };
      setFormData(mergedData);
      
      // Clear the navigation state to prevent re-applying on re-renders
      window.history.replaceState({}, document.title);
    }
    // Only run once when prefillDraft is present and form is ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillDraft, dataLoading, invoiceConfig]);

  // FEATURE #7: Backend-driven calculations
  // Automatically recalculates totals via /api/invoices/calculate when form data changes
  const { isCalculating } = useCalculations(formData, setFormData);

  // FEATURE #7: Backend validation - validate form data against backend rules
  // Debounced call to backend when form data changes
  useEffect(() => {
    if (formData && invoiceType && !dataLoading) {
      validation.validateWithBackendDebounced(formData, invoiceType, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, invoiceType, dataLoading]);

  // Memoize auto-save data to prevent unnecessary re-renders
  const autoSaveData = useMemo(() => ({
    formData,
    logoPreview,
    signaturePreview,
  }), [formData, logoPreview, signaturePreview]);
  
  // Auto-save with debounce
  useAutoSave(
    autoSaveData,
    autoSaveKey,
    AUTOSAVE_DELAY_MS,
    setIsSaving
  );

  // Update last saved indicator when saving completes
  useEffect(() => {
    if (!isSaving) {
      const draft = loadSavedData(autoSaveKey);
      if (draft?.timestamp) {
        setLastSaved(draft.timestamp);
      }
    }
  }, [isSaving, autoSaveKey]);

  /**
   * Recover saved draft
   */
  const recoverDraft = useCallback(() => {
    if (savedDraft?.formData) {
      setFormData(savedDraft.formData);
      if (savedDraft.logoPreview) setLogoPreview(savedDraft.logoPreview);
      if (savedDraft.signaturePreview) setSignaturePreview(savedDraft.signaturePreview);
      setShowRecoveryDialog(false);
    }
  }, [savedDraft, setFormData]);

  /**
   * Discard saved draft and start fresh
   */
  const discardDraft = useCallback(() => {
    clearSavedData(autoSaveKey);
    setShowRecoveryDialog(false);
    setSavedDraft(null);
  }, [autoSaveKey]);

  /**
   * Resize and compress uploaded image
   */
  const resizeImage = useCallback((base64Str, callback) => {
    const img = new Image();
    img.src = base64Str;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const { width: maxWidth, height: maxHeight } = LOGO_MAX_DIMENSIONS;

      let { width, height } = img;

      // Scale down maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round(height * (maxWidth / width));
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round(width * (maxHeight / height));
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const resizedBase64 = canvas.toDataURL('image/png', 1.0);
      callback(resizedBase64);
    };

    img.onerror = () => {
      console.error('Failed to load image for resizing');
    };
  }, []);

  /**
   * Show toast notification
   */
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  /**
   * Close toast notification
   */
  const closeToast = useCallback(() => {
    setToast(null);
  }, []);

  const handleClearAll = useCallback(() => {
    resetForm();
    setLogoPreview(null);
    setSignaturePreview(null);
    clearSavedData(autoSaveKey);
    showToast('All fields cleared', 'success');
  }, [resetForm, autoSaveKey, showToast]);

  // Close the type dropdown when clicking outside
  useEffect(() => {
    if (!showTypeDropdown) return;
    const handleClick = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTypeDropdown]);

  const handleTypeSwitch = useCallback((typeId) => {
    if (typeId === invoiceType) { setShowTypeDropdown(false); return; }
    setPendingSwitchType(typeId);
    setShowTypeDropdown(false);
  }, [invoiceType]);

  const handleSwitchConfirm = useCallback((keepData) => {
    if (!pendingSwitchType) return;
    if (keepData) {
      // GST invoices are India-only and only support INR. If the current
      // currency is something else, reset it so the user doesn't get a
      // backend validation error on their first generate attempt.
      const currencyOverride =
        pendingSwitchType === 'gst' && formData.currency !== 'INR'
          ? { currency: 'INR', currency_symbol: '₹' }
          : {};
      navigate(`/create/${pendingSwitchType}`, {
        state: { prefillDraft: { ...formData, invoice_type: pendingSwitchType, ...currencyOverride } },
      });
    } else {
      navigate(`/create/${pendingSwitchType}`);
    }
    setPendingSwitchType(null);
  }, [pendingSwitchType, formData, navigate]);

  const handleSwitchCancel = useCallback(() => {
    setPendingSwitchType(null);
  }, []);

  /**
   * Handle logo file upload
   */
  const handleLogoUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file.', 'error');
      return;
    }
    
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      showToast('Image is too large. Please upload an image smaller than 2MB.', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      resizeImage(reader.result, (resizedImage) => {
        setLogoPreview(resizedImage);
        updateFormData({ business_logo: resizedImage });
      });
    };
    reader.onerror = () => {
      showToast('Failed to read the image file.', 'error');
    };
    reader.readAsDataURL(file);
  }, [resizeImage, updateFormData, showToast]);

  /**
   * Handle logo removal
   */
  const handleApplyProfile = useCallback(() => {
    const prof = profiles.find(p => String(p.id) === pickerProfileId);
    if (!prof) return;
    const BIZ_FIELDS = [
      'business_name', 'business_email', 'business_phone', 'business_address',
      'business_city', 'business_state', 'business_zip', 'business_country', 'business_gstin',
    ];
    setFormData(prev => {
      const patched = { ...prev };
      BIZ_FIELDS.forEach(k => { if (prof[k]) patched[k] = prof[k]; });
      if (prof.business_logo) patched.business_logo = prof.business_logo;
      patched.profile_id = prof.id;
      return patched;
    });
    if (prof.business_logo) setLogoPreview(prof.business_logo);
    setShowPicker(false);
  }, [profiles, pickerProfileId, setFormData]);

  const handleApplyClient = useCallback(() => {
    const cl = clients.find(c => String(c.id) === pickerClientId);
    if (!cl) return;
    const CLIENT_FIELDS = [
      'client_name', 'client_email', 'client_phone', 'client_address',
      'client_city', 'client_state', 'client_zip', 'client_country', 'client_gstin',
    ];
    setFormData(prev => {
      const patched = { ...prev };
      CLIENT_FIELDS.forEach(k => { if (cl[k]) patched[k] = cl[k]; });
      return patched;
    });
    setShowClientPicker(false);
  }, [clients, pickerClientId, setFormData]);

  const handleLogoRemove = useCallback(() => {
    setLogoPreview(null);
    updateFormData({ business_logo: '' });
  }, [updateFormData]);

  const handleSignatureUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file.', 'error');
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      showToast('Image is too large. Please upload an image smaller than 2MB.', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      resizeImage(reader.result, (resized) => {
        setSignaturePreview(resized);
        updateFormData({ signature_image: resized });
      });
    };
    reader.onerror = () => showToast('Failed to read the image file.', 'error');
    reader.readAsDataURL(file);
  }, [resizeImage, updateFormData, showToast]);

  const handleSignatureRemove = useCallback(() => {
    setSignaturePreview(null);
    updateFormData({ signature_image: '' });
  }, [updateFormData]);

  /**
   * Build the cleaned request payload shared between finalize and guest endpoints.
   */
  const buildRequestData = useCallback(() => {
    const requestData = {
      ...formData,
      line_items: formData.items.map(item => ({
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 0,
        rate: parseFloat(item.rate) || 0,
      })),
    };
    delete requestData.items;
    delete requestData.subtotal;
    delete requestData.tax_amount;
    delete requestData.total;
    delete requestData.invoice_number;
    return requestData;
  }, [formData]);

  // Validate form before any PDF action — shared by download and print.
  const validateForAction = useCallback(() => {
    const { isValid, errors: validationErrors } = validation.validateAll(formData);
    if (!isValid) {
      const messages = Object.values(validationErrors).filter(Boolean);
      const shown = messages.slice(0, 2).join(' · ');
      const extra = messages.length > 2 ? ` (+${messages.length - 2} more)` : '';
      showToast(shown ? `${shown}${extra}` : 'Please fix the highlighted fields', 'error');
      return false;
    }
    if (formData.invoice_type === 'gst') {
      const gstMissing = [];
      if (!formData.business_gstin?.trim()) gstMissing.push('Seller GSTIN');
      if (!formData.business_state?.trim()) gstMissing.push('Seller Registered State');
      if (!formData.place_of_supply?.trim()) gstMissing.push('Place of Supply (Buyer State)');
      if (gstMissing.length > 0) {
        showToast(`GST Invoice requires: ${gstMissing.join(', ')}`, 'error');
        return false;
      }
    }
    const { canGenerate, missingFields } = validateForPdfGeneration(formData);
    if (!canGenerate) {
      showToast(`Missing required fields: ${missingFields.join(', ')}`, 'error');
      return false;
    }
    return true;
  }, [formData, validation, showToast]);

  /**
   * Finalize the invoice exactly once per form state.
   * Caches { blob, filename } after the first successful API call so that
   * Print and Download share a single backend record and invoice number.
   * The cache is keyed on the formData object reference — any field edit
   * produces a new reference, clearing the cache automatically.
   */
  const getOrFinalize = useCallback(async () => {
    const invoiceType = formData.invoice_type || 'freelancer';

    if (finalizedPDF && finalizedAtRef.current === formData) {
      return finalizedPDF;
    }

    const requestData = buildRequestData();
    const result = user
      ? await finalizeInvoice({ invoice_type: invoiceType, data: requestData, draft_id: activeDraftId })
      : await generateGuestInvoice({ invoice_type: invoiceType, data: requestData });

    if (result.blob) {
      const cached = { blob: result.blob, filename: result.filename };
      setFinalizedPDF(cached);
      finalizedAtRef.current = formData;
      clearSavedData(autoSaveKey);
      if (activeDraftId) setActiveDraftId(null);
      if (user) refreshEntitlements();
      return cached;
    }

    return result; // { valid: false, errors }
  }, [formData, user, buildRequestData, autoSaveKey, refreshEntitlements, finalizedPDF, activeDraftId]);

  const handleDownloadPDF = useCallback(async () => {
    if (!validateForAction()) return;
    if (!user && isLimitReached) { setShowSignUpGate(true); return; }

    setLoading(true);
    try {
      const result = await getOrFinalize();
      if (result.blob) {
        downloadPDF(result.blob, result.filename);
        showToast('Invoice downloaded successfully!', 'success');
        if (!user) {
          saveGuestDraft(formData.invoice_type || 'freelancer', buildRequestData());
          increment();
          setShowSignUpGate(true);
        }
      } else if (result.valid === false) {
        showToast(`Validation failed: ${result.errors.map(e => e.message).join(', ')}`, 'error');
      } else {
        showToast('Unexpected response from server', 'error');
      }
    } catch (error) {
      if (error.status === 402) {
        const { used = '?', limit = 5 } = error.detail || {};
        setUpgradeModal({ open: true, reason: `You've used ${used} of ${limit} free invoices this month. Upgrade to Pro for unlimited invoices.` });
      } else {
        showToast(error.message || 'Failed to generate invoice. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [validateForAction, getOrFinalize, user, isLimitReached, formData, buildRequestData, increment, showToast]);

  /**
   * Handle update invoice — edit mode only.
   * Validates, then calls PUT /api/invoices/{id} to regenerate the PDF in-place.
   */
  const handleUpdateInvoice = useCallback(async () => {
    const { isValid, errors: validationErrors } = validation.validateAll(formData);
    if (!isValid) {
      const messages = Object.values(validationErrors).filter(Boolean);
      const shown = messages.slice(0, 2).join(' · ');
      const extra = messages.length > 2 ? ` (+${messages.length - 2} more)` : '';
      showToast(shown ? `${shown}${extra}` : 'Please fix the highlighted fields before saving', 'error');
      return;
    }

    if (formData.invoice_type === 'gst') {
      const gstMissing = [];
      if (!formData.business_gstin?.trim()) gstMissing.push('Seller GSTIN');
      if (!formData.business_state?.trim()) gstMissing.push('Seller Registered State');
      if (!formData.place_of_supply?.trim()) gstMissing.push('Place of Supply (Buyer State)');
      if (gstMissing.length > 0) {
        showToast(`GST Invoice requires: ${gstMissing.join(', ')}`, 'error');
        return;
      }
    }

    const { canGenerate, missingFields } = validateForPdfGeneration(formData);
    if (!canGenerate) {
      showToast(`Missing required fields: ${missingFields.join(', ')}`, 'error');
      return;
    }

    setLoading(true);
    try {
      const requestData = buildRequestData();
      const result = await updateInvoice(editInvoiceId, {
        invoice_type: formData.invoice_type || invoiceType,
        data: requestData,
      });

      if (result.blob) {
        downloadPDF(result.blob, result.filename);
        showToast('Invoice updated and downloaded successfully!', 'success');
        refreshEntitlements();
        setTimeout(() => navigate(`/history/${editInvoiceId}`), 1500);
      } else if (result.valid === false) {
        const errorMessages = result.errors.map(err => err.message).join(', ');
        showToast(`Validation failed: ${errorMessages}`, 'error');
      } else {
        showToast('Unexpected response from server', 'error');
      }
    } catch (error) {
      console.error('Invoice update error:', error);
      showToast(error.message || 'Error updating invoice. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, validation, showToast, editInvoiceId, invoiceType, buildRequestData, navigate, refreshEntitlements]);

  const handlePrint = useCallback(async () => {
    if (!validateForAction()) return;
    if (!user && isLimitReached) { setShowSignUpGate(true); return; }

    // Open the tab SYNCHRONOUSLY here, while we are still inside the user
    // gesture handler. Calling window.open() after any await causes browsers
    // to treat it as a programmatic popup and block it on the first click.
    const win = window.open('about:blank', '_blank');
    if (!win) {
      showToast('Pop-up blocked — please allow pop-ups for this site and try again.', 'error');
      return;
    }

    setLoading(true);
    try {
      const result = await getOrFinalize();
      if (result.blob) {
        const url = URL.createObjectURL(result.blob);
        // Navigate the already-opened blank tab to the blob URL.
        win.location.replace(url);
        win.focus();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        if (!user) {
          saveGuestDraft(formData.invoice_type || 'freelancer', buildRequestData());
          increment();
          setShowSignUpGate(true);
        }
      } else {
        win.close();
        if (result.valid === false) {
          showToast(`Validation failed: ${result.errors.map(e => e.message).join(', ')}`, 'error');
        } else {
          showToast('Could not generate print preview', 'error');
        }
      }
    } catch (error) {
      win.close();
      if (error.status === 402) {
        const { used = '?', limit = 5 } = error.detail || {};
        setUpgradeModal({ open: true, reason: `You've used ${used} of ${limit} free invoices this month. Upgrade to Pro for unlimited invoices.` });
      } else {
        showToast('Failed to generate print preview. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [validateForAction, getOrFinalize, user, isLimitReached, formData, buildRequestData, increment, showToast]);

  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleEmail = useCallback(() => {
    if (!user) {
      showToast('Sign in to email invoices to clients', 'error');
      return;
    }
    if (!isFeatureEnabled(Features.INVOICE_EMAIL)) {
      setUpgradeModal({ open: true, reason: 'Emailing invoices directly to clients is a Pro feature. Upgrade to unlock it.' });
      return;
    }
    setShowEmailModal(true);
  }, [user, isFeatureEnabled, showToast]);

  const handleSaveDraft = useCallback(async () => {
    if (!user) {
      showToast('Sign in to save drafts', 'error');
      return;
    }
    if (!canSaveDraft) {
      setUpgradeModal({ open: true, reason: 'Saving drafts to your history is a Pro feature. Upgrade to keep work in progress across sessions.' });
      return;
    }
    setDraftSaving(true);
    try {
      const result = await saveDraft({
        invoice_type: invoiceType,
        data: buildRequestData(),
        draft_id: activeDraftId,
      });
      if (!activeDraftId) setActiveDraftId(result.id);
      showToast('Draft saved', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to save draft', 'error');
    } finally {
      setDraftSaving(false);
    }
  }, [user, canSaveDraft, invoiceType, buildRequestData, activeDraftId, showToast]);

  // Loading state
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading invoice configuration...</p>
        </div>
      </div>
    );
  }

  // Error state - show when backend config fails to load
  if (configError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Configuration Error</h2>
          <p className="text-gray-600 mb-4">{configError}</p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Navigation Header */}
      <div className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <Link
                to="/"
                className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-medium"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <div className="hidden sm:block h-5 w-px bg-gray-200 dark:bg-gray-700" />
              {user && (
                <>
                  <Link
                    to="/clients"
                    className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-medium"
                    title="Client Address Book"
                  >
                    <Users size={15} />
                    <span className="hidden sm:inline">Clients</span>
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-medium"
                    title="Business Profiles"
                  >
                    <Building2 size={15} />
                    <span className="hidden sm:inline">Profile</span>
                  </Link>
                </>
              )}
              <div className="hidden sm:block h-5 w-px bg-gray-200 dark:bg-gray-700" />

              {/* Invoice type indicator / switcher */}
              <div className="relative min-w-0" ref={typeDropdownRef}>
                {(isEditMode || activeDraftId) ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: invoiceConfig?.theme?.primaryColor || '#3B82F6' }}
                    />
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[110px] sm:max-w-none">
                      {invoiceConfig?.name || uiMetadata.name || 'Invoice'}
                    </span>
                    {editInvoiceNumber && (
                      <span className="hidden sm:inline text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full font-medium">
                        Editing {editInvoiceNumber}
                      </span>
                    )}
                    {activeDraftId && !isEditMode && (
                      <span className="hidden sm:inline text-xs px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 rounded-full font-medium">
                        Draft
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowTypeDropdown(v => !v)}
                    className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: invoiceConfig?.theme?.primaryColor || '#3B82F6' }}
                    />
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[110px] sm:max-w-none">
                      {invoiceConfig?.name || uiMetadata.name || 'Invoice'}
                    </span>
                    <ChevronDown
                      size={13}
                      className={`text-gray-400 transition-transform duration-200 ${showTypeDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}

                {/* Type switcher dropdown */}
                {showTypeDropdown && (
                  <div className="absolute left-0 top-full mt-1.5 w-56 sm:w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 overflow-hidden py-1.5">
                    {Object.values(INVOICE_TYPES_UI).map((type) => (
                      <button
                        key={type.id}
                        onClick={() => handleTypeSwitch(type.id)}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors ${
                          type.id === invoiceType
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: type.theme.primaryColor }}
                        />
                        <span className={`flex-1 text-sm font-medium truncate ${
                          type.id === invoiceType
                            ? 'text-blue-700 dark:text-blue-400'
                            : 'text-gray-800 dark:text-gray-200'
                        }`}>
                          {type.name}
                        </span>
                        {type.id === invoiceType && (
                          <CheckCircle size={13} className="text-blue-500 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!isEditMode && (
                <button
                  onClick={handleClearAll}
                  title="Clear all fields"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Eraser size={14} />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              )}
              <Link
                to="/"
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Home"
              >
                <Home size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Draft Recovery Dialog */}
      {showRecoveryDialog && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={discardDraft}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close dialog"
            >
              <X size={24} />
            </button>

            <div className="flex items-start space-x-4 mb-4">
              <div className="bg-blue-100 rounded-full p-3">
                <AlertCircle className="text-blue-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Resume Previous Invoice?
                </h3>
                <p className="text-gray-600 mb-4">
                  We found a saved draft. Would you like to continue editing it?
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={discardDraft}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={recoverDraft}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice type switch confirmation dialog */}
      {pendingSwitchType && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-w-sm w-full p-6 relative">
            <button
              onClick={handleSwitchCancel}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Cancel"
            >
              <X size={18} />
            </button>

            <div className="mb-5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${INVOICE_TYPES_UI[pendingSwitchType]?.theme.primaryColor}22` }}
              >
                <span
                  className="w-3.5 h-3.5 rounded-full"
                  style={{ backgroundColor: INVOICE_TYPES_UI[pendingSwitchType]?.theme.primaryColor }}
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                Switch to {INVOICE_TYPES_UI[pendingSwitchType]?.name}?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You have data in the current form. How would you like to continue?
              </p>
            </div>

            <div className="space-y-2.5">
              <button
                onClick={() => handleSwitchConfirm(true)}
                className="w-full flex items-start gap-3 p-3.5 border-2 border-blue-500 dark:border-blue-600 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
              >
                <div className="mt-0.5 w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={15} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Keep my data</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Copy your filled fields to the new invoice type
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleSwitchConfirm(false)}
                className="w-full flex items-start gap-3 p-3.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
              >
                <div className="mt-0.5 w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <X size={15} className="text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Start fresh</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Open a blank form for {INVOICE_TYPES_UI[pendingSwitchType]?.name}
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={handleSwitchCancel}
              className="w-full mt-3 text-sm text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors py-1"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Profile Picker Banner */}
      {showPicker && profiles.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-4 py-3 flex flex-wrap items-center gap-3 print:hidden">
          <Building2 size={15} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200 whitespace-nowrap">
            Business profile:
          </span>
          <select
            value={pickerProfileId}
            onChange={e => setPickerProfileId(e.target.value)}
            className="flex-1 min-w-[140px] max-w-xs text-sm border border-blue-300 dark:border-blue-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          >
            {profiles.map(p => (
              <option key={p.id} value={String(p.id)}>{p.profile_name}</option>
            ))}
          </select>
          <button onClick={handleApplyProfile} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
            Apply
          </button>
          <button onClick={() => setShowPicker(false)} className="text-sm text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-200 px-1 transition-colors">
            Skip
          </button>
        </div>
      )}

      {/* Client Picker Banner */}
      {showClientPicker && clients.length > 0 && (
        <div className="bg-violet-50 dark:bg-violet-900/20 border-b border-violet-200 dark:border-violet-800 px-4 py-3 flex flex-wrap items-center gap-3 print:hidden">
          <Users size={15} className="text-violet-600 dark:text-violet-400 flex-shrink-0" />
          <span className="text-sm font-medium text-violet-800 dark:text-violet-200 whitespace-nowrap">
            Fill client from:
          </span>
          <select
            value={pickerClientId}
            onChange={e => setPickerClientId(e.target.value)}
            className="flex-1 min-w-[140px] max-w-xs text-sm border border-violet-300 dark:border-violet-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/40"
          >
            {clients.map(c => (
              <option key={c.id} value={String(c.id)}>{c.client_name}</option>
            ))}
          </select>
          <button onClick={handleApplyClient} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-lg transition-colors">
            Apply
          </button>
          <button onClick={() => setShowClientPicker(false)} className="text-sm text-violet-500 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-200 px-1 transition-colors">
            Skip
          </button>
        </div>
      )}

      {/* Main Invoice Canvas */}
      <InvoiceCanvas
        formData={formData}
        updateFormData={updateFormData}
        onDownloadPDF={isEditMode ? handleUpdateInvoice : handleDownloadPDF}
        downloadLabel={isEditMode ? 'Save Changes' : undefined}
        onPrint={handlePrint}
        onEmail={handleEmail}
        onSaveDraft={handleSaveDraft}
        canSaveDraft={canSaveDraft}
        draftSaving={draftSaving}
        isDraftMode={Boolean(activeDraftId)}
        lastSaved={lastSaved}
        isSaving={isSaving}
        logoPreview={logoPreview}
        onLogoUpload={handleLogoUpload}
        onLogoRemove={handleLogoRemove}
        signaturePreview={signaturePreview}
        onSignatureUpload={handleSignatureUpload}
        onSignatureRemove={handleSignatureRemove}
        loading={loading}
        validation={validation}
        invoiceConfig={invoiceConfig}
        isCalculating={isCalculating}
        isValidating={validation.isValidating}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
          duration={5000}
        />
      )}

      {/* Sign-up gate: hard block when limit is reached, soft prompt after first invoice */}
      {showSignUpGate && (
        <SignUpGate
          onClose={isLimitReached ? null : () => setShowSignUpGate(false)}
        />
      )}

      {/* Upgrade modal: shown when authenticated free user hits monthly limit */}
      {upgradeModal.open && (
        <UpgradeModal
          reason={upgradeModal.reason}
          onClose={() => setUpgradeModal({ open: false, reason: '' })}
        />
      )}

      {/* Email invoice modal */}
      {showEmailModal && (
        <EmailInvoiceModal
          formData={formData}
          invoiceType={invoiceType}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
}